import React, { useEffect, useMemo, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  TbBox,
  TbTruckDelivery,
  TbTruckReturn,
  TbPackageExport,
} from "react-icons/tb";
import { BiPurchaseTag } from "react-icons/bi";
import backendVerifiedUser from "../../api/backendVerifiedUser";
import DashboardTiles from "../Dashboard/DashboardTiles";
import ReportsChart from "./ReportsChart";
import ReportsDateFilter from "./ReportsDateFilter";
import { computeDateRange } from "./reportFilters";

const STATUS_OPTIONS = [
  { name: "All Statuses", value: "" },
  { name: "Pending", value: "pending" },
  { name: "To Pack", value: "to_pack" },
  { name: "Arrange Shipment", value: "arrange_shipment" },
  { name: "Hand Over", value: "hand_over" },
  { name: "Shipping", value: "shipping" },
  { name: "Delivered", value: "delivered" },
  { name: "Returned", value: "returned" },
  { name: "Cancelled", value: "cancelled" },
];

export default function OrdersReportTab() {
  const today = useMemo(() => new Date(), []);
  const [rangeMode, setRangeMode] = useState("month");
  const [singleDate, setSingleDate] = useState(today);
  const [dateRange, setDateRange] = useState([
    new Date(today.getFullYear(), today.getMonth(), 1),
    today,
  ]);
  const [statusFilter, setStatusFilter] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const { startDate, endDate } = useMemo(
    () => computeDateRange(rangeMode, today, singleDate, dateRange),
    [rangeMode, singleDate, dateRange, today]
  );

  useEffect(() => {
    let cancelled = false;
    const fetchReport = async () => {
      setLoading(true);
      try {
        const params = {};
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        if (statusFilter) params.status = statusFilter;

        const response = await backendVerifiedUser.get("/reports/orders", { params });
        if (!cancelled) setReport(response.data);
      } catch (err) {
        console.log(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchReport();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, statusFilter]);

  const summary = report?.summary || {};

  const kpiData = [
    { amount: summary.totalOrders || 0, title: "Total Orders", icon: <TbBox /> },
    { amount: summary.totalSales || 0, title: "Total Sales", icon: <BiPurchaseTag />, prefix: "RS" },
    { amount: summary.deliveredOrders || 0, title: "Delivered Orders", icon: <TbTruckDelivery /> },
    { amount: summary.shippingOrders || 0, title: "Shipping Orders", icon: <TbPackageExport /> },
    { amount: summary.handoverOrders || 0, title: "Handover Orders", icon: <TbPackageExport /> },
    { amount: summary.returnedOrders || 0, title: "Returned Orders", icon: <TbTruckReturn /> },
  ];

  const secondaryKpiData = [
    { amount: summary.pendingOrders || 0, title: "Pending Orders", icon: <TbBox /> },
    { amount: summary.toPackOrders || 0, title: "To Pack Orders", icon: <TbBox /> },
    { amount: summary.arrangeShipmentOrders || 0, title: "Arrange Shipment Orders", icon: <TbBox /> },
    { amount: summary.cancelledOrders || 0, title: "Cancelled Orders", icon: <TbBox /> },
  ];

  const handleExport = () => {
    if (!report) return;

    const summaryRows = [
      ["Metric", "Value"],
      ["Date Range", `${startDate || "-"} to ${endDate || "-"}`],
      ["Status Filter", statusFilter || "All"],
      ["Total Orders", summary.totalOrders || 0],
      ["Total Sales", summary.totalSales || 0],
      ["Delivered Orders", summary.deliveredOrders || 0],
      ["Delivered Sales", summary.deliveredSales || 0],
      ["Shipping Orders", summary.shippingOrders || 0],
      ["Handover Orders", summary.handoverOrders || 0],
      ["Returned Orders", summary.returnedOrders || 0],
      ["Returned Sales", summary.returnedSales || 0],
      ["Pending Orders", summary.pendingOrders || 0],
      ["To Pack Orders", summary.toPackOrders || 0],
      ["Arrange Shipment Orders", summary.arrangeShipmentOrders || 0],
      ["Cancelled Orders", summary.cancelledOrders || 0],
    ];

    const ordersRows = [
      ["Order #", "Status", "Payment Method", "Paid", "Customer", "Contact", "Total Amount", "Created At"],
      ...(report.orders || []).map((o) => [
        o.order_number,
        o.status,
        o.payment_method,
        o.payment_status ? "Yes" : "No",
        o.customer_name,
        o.contact,
        o.total_amount,
        o.created_at,
      ]),
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryRows), "Summary");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(ordersRows), "Orders");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `orders-report-${startDate || "all"}-to-${endDate || "all"}.xlsx`);
  };

  return (
    <div>
      <div className="flex items-center justify-end mb-3">
        <Button
          label="Export to Excel"
          icon="pi pi-file-excel"
          className="p-primary-btn"
          onClick={handleExport}
          disabled={!report}
        />
      </div>

      <ReportsDateFilter
        rangeMode={rangeMode}
        setRangeMode={setRangeMode}
        singleDate={singleDate}
        setSingleDate={setSingleDate}
        dateRange={dateRange}
        setDateRange={setDateRange}
        loading={loading}
      >
        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-500">Order Status</label>
          <Dropdown
            value={statusFilter}
            options={STATUS_OPTIONS}
            optionLabel="name"
            optionValue="value"
            onChange={(e) => setStatusFilter(e.value)}
            className="w-56"
          />
        </div>
      </ReportsDateFilter>

      {report && (
        <>
          <div className="mb-3 font-semibold text-black/50 text-lg ml-1">Summary</div>
          <DashboardTiles data={kpiData} />

          <div className="mt-6 mb-3 font-semibold text-black/50 text-lg ml-1">Other Statuses</div>
          <DashboardTiles data={secondaryKpiData} />

          <div className="mt-8">
            <div className="my-3 font-semibold text-black/50 text-xl ml-1">Orders &amp; Sales Trend</div>
            <div className="card shadow-md p-4">
              {report.chartData && report.chartData.length > 0 ? (
                <ReportsChart data={report.chartData} />
              ) : (
                <div className="text-gray-400 text-sm py-8 text-center">No data for the selected filters.</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
